import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isoDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Combobox from "@/components/Combobox";
import ScanDocumentBox from "@/components/ScanDocumentBox";
import { MARQUES, getModelesForMarque } from "@/lib/marques";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import NewClientDialog from "@/components/NewClientDialog";
import { Plus, Trash2, Eye, Search, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import Plate from "@/components/Plate";
import { downloadXlsx, fmtDate, fmtBool, todayStamp } from "@/lib/csv";

const empty = {
  client_id: "", client_nom: "", marque: "", modele: "", annee: "",
  plaque: "", vin: "", couleur: "", kilometrage: "", prix_vente: "",
  numero_vo: "",
  date_vente: isoDate(),
  date_depart: "",
  carte_grise_disponible: false,
  notes: "",
  acompte_montant: "",
  acompte_methode: "Espèces",
};

export default function Ventes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCommercial = user?.role === "commercial";
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [statut, setStatut] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date-desc");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (statut !== "all") params.statut = statut;
    const { data } = await api.get("/ventes", { params });
    // Par défaut, exclure les ventes payées (elles sont dans VO Vendus)
    const filtered = statut === "all" ? data.filter((v) => v.statut !== "paye") : data;
    setItems(filtered);
    setLoading(false);
  };

  const loadClients = async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadClients(); }, []);

  // Live client-side search across plaque, client name, marque, modele, numero_vo, VIN
  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = items;
    if (term) {
      const compactPlate = term.replace(/[\s-]/g, "");
      list = items.filter((v) => {
        const plate = (v.plaque || "").toLowerCase();
        return (
          plate.includes(compactPlate) ||
          (v.client_nom || "").toLowerCase().includes(term) ||
          (v.marque || "").toLowerCase().includes(term) ||
          (v.modele || "").toLowerCase().includes(term) ||
          (v.numero_vo || "").toLowerCase().includes(term) ||
          (v.vin || "").toLowerCase().includes(term)
        );
      });
    }
    // Sort
    const arr = [...list];
    switch (sortBy) {
      case "date-asc":
        arr.sort((a, b) => (a.date_vente || "").localeCompare(b.date_vente || ""));
        break;
      case "prix-desc":
        arr.sort((a, b) => Number(b.prix_vente || 0) - Number(a.prix_vente || 0));
        break;
      case "prix-asc":
        arr.sort((a, b) => Number(a.prix_vente || 0) - Number(b.prix_vente || 0));
        break;
      case "restant-desc":
        arr.sort((a, b) => Number(b.montant_restant || 0) - Number(a.montant_restant || 0));
        break;
      case "restant-asc":
        arr.sort((a, b) => Number(a.montant_restant || 0) - Number(b.montant_restant || 0));
        break;
      case "client":
        arr.sort((a, b) => (a.client_nom || "").localeCompare(b.client_nom || "", "fr"));
        break;
      case "marque":
        arr.sort((a, b) =>
          (a.marque || "").localeCompare(b.marque || "", "fr") ||
          (a.modele || "").localeCompare(b.modele || "", "fr")
        );
        break;
      case "date-depart":
        arr.sort((a, b) => (a.date_depart || "9999").localeCompare(b.date_depart || "9999"));
        break;
      case "date-desc":
      default:
        arr.sort((a, b) => (b.date_vente || "").localeCompare(a.date_vente || ""));
    }
    return arr;
  }, [items, search, sortBy]);

  const startCreate = () => { setForm(empty); setOpen(true); };

  const handleClientChange = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm((f) => ({ ...f, client_id: id, client_nom: c ? c.nom : f.client_nom }));
  };

  const handleClientCreated = (newClient) => {
    setClients((prev) => [newClient, ...prev.filter((c) => c.id !== newClient.id)]);
    setForm((f) => ({ ...f, client_id: newClient.id, client_nom: newClient.nom }));
    toast.success(`Client ${newClient.nom} lié à la vente`);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        annee: form.annee ? Number(form.annee) : null,
        kilometrage: form.kilometrage ? Number(form.kilometrage) : 0,
        prix_vente: Number(form.prix_vente || 0),
        client_id: form.client_id || null,
      };
      await api.post("/ventes", payload);
      toast.success("Vente enregistrée");
      setOpen(false); load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      const text = Array.isArray(msg)
        ? msg.map((m) => m.msg || JSON.stringify(m)).join(" · ")
        : typeof msg === "string" ? msg : "Erreur lors de la création";
      toast.error(text);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Supprimer la vente ${v.plaque} ?\n\nElle sera placée dans la Corbeille pendant 7 jours, puis effacée définitivement.`)) return;
    await api.delete(`/ventes/${v.id}`);
    toast.success("Vente placée dans la Corbeille (7 jours)"); load();
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Transactions · en cours
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Ventes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ventes non soldées · Les ventes payées sont archivées dans{" "}
            <a href="/vo-vendus" className="text-klein hover:underline font-medium">VO vendus</a>.
          </p>
          {!isCommercial && items.length > 0 && (
            <p className="text-sm mt-2" data-testid="ventes-total-creance">
              Créance totale : <span className="font-display font-bold text-destructive">
                {money(filteredItems.reduce((s, v) => s + Number(v.montant_restant || 0), 0))}
              </span>
              {" · "}
              CA en cours : <span className="font-display font-bold">
                {money(filteredItems.reduce((s, v) => s + Number(v.prix_vente || 0), 0))}
              </span>
              {search && (
                <span className="text-muted-foreground"> · {filteredItems.length}/{items.length} résultat{filteredItems.length > 1 ? "s" : ""}</span>
              )}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="ventes-search"
              type="search"
              name="ventes-search"
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="pl-9 rounded-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="ventes-search-clear"
              >
                ✕
              </button>
            )}
          </div>
          <Select value={statut} onValueChange={(v) => { setStatut(v); }}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="ventes-filter-statut">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="paye">Payé</SelectItem>
              <SelectItem value="partiel">Partiel</SelectItem>
              <SelectItem value="impaye">Impayé</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="ventes-apply-filter">Filtrer</Button>
          <select
            data-testid="ventes-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-10 rounded-sm border border-input bg-background px-3 text-sm"
            title="Trier les ventes"
          >
            <option value="date-desc">Date · plus récent</option>
            <option value="date-asc">Date · plus ancien</option>
            <option value="date-depart">Date départ · plus proche</option>
            <option value="prix-desc">Prix · décroissant</option>
            <option value="prix-asc">Prix · croissant</option>
            <option value="restant-desc">Restant dû · décroissant</option>
            <option value="restant-asc">Restant dû · croissant</option>
            <option value="client">Client (A→Z)</option>
            <option value="marque">Marque (A→Z)</option>
          </select>

          {!isCommercial && (
            <Button
              type="button"
              variant="outline"
              data-testid="ventes-export-csv"
              onClick={() => {
                if (items.length === 0) { toast.error("Aucune vente à exporter"); return; }
                const columns = [
                  { key: "date_vente", label: "Date vente", format: fmtDate },
                  { key: "date_depart", label: "Date départ", format: fmtDate },
                  { key: "numero_vo", label: "N° VO" },
                  { key: "plaque", label: "Plaque" },
                  { key: "marque", label: "Marque" },
                  { key: "modele", label: "Modèle" },
                  { key: "annee", label: "Année", type: "number" },
                  { key: "client_nom", label: "Client" },
                  { key: "client_telephone", label: "Téléphone" },
                  { key: "prix_vente", label: "Prix vente", type: "number" },
                  { key: "cout_interventions", label: "Coût mécanique", type: "number" },
                  { key: "prix_total", label: "Total à payer", type: "number" },
                  { key: "montant_paye", label: "Encaissé", type: "number" },
                  { key: "montant_restant", label: "Restant dû", type: "number" },
                  { key: "statut", label: "Statut" },
                  { key: "carte_grise_disponible", label: "Carte grise", format: fmtBool },
                  { key: "preparer_a_livrer", label: "Prêt à livrer", format: fmtBool },
                  { key: "vin", label: "VIN" },
                  { key: "couleur", label: "Couleur" },
                  { key: "kilometrage", label: "Kilométrage", type: "number" },
                  { key: "notes", label: "Notes" },
                ];
                downloadXlsx(`ventes-en-cours-${todayStamp()}`, columns, items, "Ventes en cours");
                toast.success(`${items.length} vente(s) exportée(s) (Excel)`);
              }}
              className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Download size={14} className="mr-1" /> Excel
            </Button>
          )}

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} data-testid="vente-add-btn" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                <Plus size={14} className="mr-1" /> Nouvelle vente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">Nouvelle vente</DialogTitle>
              </DialogHeader>

              {/* AI document scanner */}
              <ScanDocumentBox
                onExtract={(data) => {
                  setForm((f) => ({
                    ...f,
                    marque: data.marque || f.marque,
                    modele: data.modele || f.modele,
                    annee: data.annee || f.annee,
                    plaque: (data.plaque || f.plaque || "").replace(/-/g, "").toUpperCase(),
                    vin: data.vin || f.vin,
                    couleur: data.couleur || f.couleur,
                    kilometrage: data.kilometrage ?? f.kilometrage,
                  }));
                }}
              />

              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Client existant</Label>
                    <Select value={form.client_id || undefined} onValueChange={handleClientChange}>
                      <SelectTrigger data-testid="vente-form-client" className="rounded-sm mt-1"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs uppercase tracking-wider">Nom client *</Label>
                      <Input data-testid="vente-form-clientnom" value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value, client_id: "" })} required className="rounded-sm mt-1" />
                    </div>
                    <NewClientDialog onCreated={handleClientCreated} triggerLabel="+ Nouveau" testId="vente-form-newclient" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Marque</Label>
                    <Combobox
                      testId="vente-form-marque"
                      value={form.marque}
                      onChange={(v) => setForm({ ...form, marque: v, modele: "" })}
                      options={MARQUES}
                      placeholder="Tapez ou choisissez…"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Modèle</Label>
                    <Combobox
                      testId="vente-form-modele"
                      value={form.modele}
                      onChange={(v) => setForm({ ...form, modele: v })}
                      options={getModelesForMarque(form.marque)}
                      placeholder={form.marque ? "Tapez ou choisissez…" : "Sélectionnez d'abord la marque"}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Année</Label>
                    <Input data-testid="vente-form-annee" type="number" inputMode="decimal" value={form.annee} onChange={(e) => setForm({ ...form, annee: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Plaque *</Label>
                    <Input data-testid="vente-form-plaque" value={form.plaque} onChange={(e) => setForm({ ...form, plaque: e.target.value.toUpperCase() })} required className="rounded-sm mt-1 font-mono uppercase" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">VIN</Label>
                    <Input data-testid="vente-form-vin" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Couleur</Label>
                    <Input data-testid="vente-form-couleur" value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Kilométrage</Label>
                    <Input data-testid="vente-form-km" type="number" inputMode="decimal" value={form.kilometrage} onChange={(e) => setForm({ ...form, kilometrage: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Prix de vente *</Label>
                    <Input data-testid="vente-form-prix" type="number" inputMode="decimal" step="0.01" value={form.prix_vente} onChange={(e) => setForm({ ...form, prix_vente: e.target.value })} required className="rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">N° VO</Label>
                    <Input data-testid="vente-form-numero-vo" value={form.numero_vo} onChange={(e) => setForm({ ...form, numero_vo: e.target.value })} className="rounded-sm mt-1" placeholder="Stock interne" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Date vente</Label>
                    <Input data-testid="vente-form-date" type="date" value={form.date_vente} onChange={(e) => setForm({ ...form, date_vente: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Date de départ</Label>
                    <Input data-testid="vente-form-date-depart" type="date" value={form.date_depart} onChange={(e) => setForm({ ...form, date_depart: e.target.value })} className="rounded-sm mt-1" />
                  </div>
                  <label className="flex items-center gap-2 px-3 py-2 border border-border rounded-sm cursor-pointer mt-6 select-none">
                    <input
                      type="checkbox"
                      data-testid="vente-form-cartegrise"
                      checked={!!form.carte_grise_disponible}
                      onChange={(e) => setForm({ ...form, carte_grise_disponible: e.target.checked })}
                      className="w-4 h-4 accent-klein"
                    />
                    <span className="text-sm">Carte grise disponible</span>
                  </label>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Notes</Label>
                  <Textarea data-testid="vente-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" rows={2} />
                </div>

                {/* Acompte initial (facultatif) */}
                <div className="p-3 border border-emerald-200 bg-emerald-50/40 rounded-sm">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-emerald-800 font-bold mb-2">
                    Acompte initial (facultatif)
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Montant acompte</Label>
                      <Input
                        data-testid="vente-form-acompte"
                        type="number" inputMode="decimal" step="0.01"
                        value={form.acompte_montant}
                        onChange={(e) => setForm({ ...form, acompte_montant: e.target.value })}
                        placeholder="Ex: 500"
                        className="rounded-sm mt-1 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Méthode</Label>
                      <Select value={form.acompte_methode} onValueChange={(v) => setForm({ ...form, acompte_methode: v })}>
                        <SelectTrigger data-testid="vente-form-acompte-methode" className="rounded-sm mt-1 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Espèces">Espèces</SelectItem>
                          <SelectItem value="Virement">Virement</SelectItem>
                          <SelectItem value="Chèque">Chèque</SelectItem>
                          <SelectItem value="Carte">Carte</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Si renseigné, un paiement sera automatiquement lié à cette vente lors de la création.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="vente-form-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                    Enregistrer la vente
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Actions</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">N° VO</th>
              <th className="px-3 py-2">Plaque</th>
              <th className="px-3 py-2">Véhicule</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Téléphone</th>
              <th className="px-3 py-2 text-right">Prix</th>
              <th className="px-3 py-2 text-right">Payé</th>
              <th className="px-3 py-2 text-right">Restant</th>
              <th className="px-3 py-2 text-center">Statut</th>
            </tr>
          </thead>
          <tbody data-testid="ventes-table-body">
            {loading && <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && filteredItems.length === 0 && items.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">Aucune vente. Créez-en une.</td></tr>
            )}
            {!loading && filteredItems.length === 0 && items.length > 0 && (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">
                Aucune vente ne correspond à « {search} ».
              </td></tr>
            )}
            {filteredItems.map((v) => (
              <tr key={v.id} className="border-t hover:bg-slate-50 transition-colors">
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => navigate(`/ventes/${v.id}`)} data-testid={`vente-view-${v.id}`} title="Détail" className="inline-flex items-center p-1.5 bg-klein/5 hover:bg-klein/10 text-klein border border-klein/20 rounded-sm mr-1">
                    <Eye size={14} />
                  </button>
                  <button onClick={() => remove(v)} data-testid={`vente-delete-${v.id}`} title="Supprimer" className="inline-flex items-center p-1.5 text-destructive hover:bg-red-50 rounded-sm">
                    <Trash2 size={14} />
                  </button>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{dateFR(v.date_vente)}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{v.numero_vo || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Plate value={v.plaque} small />
                    {!v.carte_grise_disponible && (
                      <span title="Carte grise manquante" data-testid={`cg-missing-${v.id}`} className="text-destructive">
                        <AlertTriangle size={14} />
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">{v.marque} {v.modele} {v.annee ? `(${v.annee})` : ""}</td>
                <td className="px-3 py-2">{v.client_nom}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {v.client_telephone
                    ? <a href={`tel:${v.client_telephone}`} className="text-klein hover:underline font-mono text-xs">{v.client_telephone}</a>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono">{money(v.prix_vente)}</td>
                <td className="px-3 py-2 text-right font-mono">{money(v.montant_paye)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-destructive">{money(v.montant_restant)}</td>
                <td className="px-3 py-2 text-center"><StatusBadge statut={v.statut} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
